/**
 * @description Reusable product pricing-mode and pricing-configuration fields.
 * @dependencies React and ProductForm-owned field/variant state callbacks.
 * @notes Mode changes away from variant pricing require confirmation and soft-deactivate variants.
 */

import React from 'react';

export const pricingTypes = Object.freeze({
  fixed: 'fixed',
  variant: 'variant',
  customOnly: 'custom_only'
});

const pricingTypeOptions = [
  { value: pricingTypes.fixed, label: 'Fixed price' },
  { value: pricingTypes.variant, label: 'Variant pricing' },
  { value: pricingTypes.customOnly, label: 'Custom amount only' }
];

export const getVariantPricingError = (variants = []) => {
  const configuredVariants = variants.filter((variant) => variant.name.trim() || variant.isActive);
  const hasInvalidVariant = configuredVariants.some((variant) => (
    !variant.name.trim()
    || variant.amount === ''
    || Number.isNaN(Number(variant.amount))
    || Number(variant.amount) < 0
  ));
  if (hasInvalidVariant) {
    return 'Each active or named variant needs a name and valid amount';
  }
  if (!configuredVariants.some((variant) => variant.isActive)) {
    return 'Add at least one active variant';
  }
  return null;
};

const PricingConfiguration = ({
  pricingType,
  price,
  allowCustomAmount,
  variants = [],
  errors = {},
  onPricingTypeChange,
  onPriceChange,
  onAllowCustomAmountChange,
  onDeactivateVariants,
  onAddVariant,
  onUpdateVariant,
  onRemoveVariant
}) => {
  const handlePricingTypeChange = (event) => {
    const nextPricingType = event.target.value;
    if (
      pricingType === pricingTypes.variant
      && nextPricingType !== pricingTypes.variant
      && variants.length > 0
    ) {
      const confirmed = window.confirm(
        'Switch pricing type? Existing variants will be kept and marked inactive.'
      );
      if (!confirmed) return;
      onDeactivateVariants();
    }
    onPricingTypeChange(nextPricingType);
  };

  return (
    <section className="md:col-span-2 border-t pt-4 space-y-4" aria-labelledby="pricing-heading">
      <div>
        <label htmlFor="pricingType" id="pricing-heading" className="block text-sm font-medium text-gray-700 mb-1">
          Pricing Type
        </label>
        <select
          id="pricingType"
          name="pricingType"
          value={pricingType}
          onChange={handlePricingTypeChange}
          className="w-full md:w-72 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand"
        >
          {pricingTypeOptions.map((option) => (
            <option key={option.value} value={option.value}>{option.label}</option>
          ))}
        </select>
      </div>

      {pricingType === pricingTypes.fixed && (
        <div className="max-w-xs">
          <label htmlFor="price" className="block text-sm font-medium text-gray-700 mb-1">
            Price <span className="text-red-500">*</span>
          </label>
          <input
            type="number"
            id="price"
            name="price"
            value={price}
            onChange={onPriceChange}
            min="0"
            step="0.01"
            placeholder="0.00"
            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-brand ${
              errors.price ? 'border-red-500' : 'border-gray-300'
            }`}
          />
          {errors.price && <p className="text-red-500 text-sm mt-1">{errors.price}</p>}
        </div>
      )}

      {pricingType === pricingTypes.variant && (
        <div>
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-gray-700">Variants</h3>
            <button
              type="button"
              onClick={onAddVariant}
              className="text-sm text-brand hover:text-brand-hover font-medium transition-colors"
            >
              + Add Variant
            </button>
          </div>

          {variants.length > 0 ? (
            <div className="space-y-3">
              {variants.map((variant) => (
                <div
                  key={variant._key}
                  className={`grid grid-cols-1 sm:grid-cols-[1fr_8rem_auto_auto] gap-2 items-center ${
                    variant.isActive ? '' : 'opacity-60'
                  }`}
                >
                  <input
                    type="text"
                    aria-label="Variant name"
                    placeholder="Variant name"
                    value={variant.name}
                    onChange={(event) => onUpdateVariant(variant._key, 'name', event.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-brand"
                  />
                  <input
                    type="number"
                    aria-label="Variant amount"
                    placeholder="Amount"
                    step="0.01"
                    min="0"
                    value={variant.amount}
                    onChange={(event) => onUpdateVariant(variant._key, 'amount', event.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-brand"
                  />
                  <label className="flex items-center text-sm text-gray-700">
                    <input
                      type="checkbox"
                      aria-label={`Active variant ${variant.name || 'new'}`}
                      checked={variant.isActive}
                      onChange={(event) => onUpdateVariant(variant._key, 'isActive', event.target.checked)}
                      className="h-4 w-4 text-brand focus:ring-brand border-gray-300 rounded"
                    />
                    <span className="ml-2">Active</span>
                  </label>
                  {!variant.id && (
                    <button
                      type="button"
                      onClick={() => onRemoveVariant(variant._key)}
                      className="text-red-500 hover:text-red-700 text-sm"
                    >
                      Remove
                    </button>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-500">Add at least one active variant.</p>
          )}
          {errors.variants && <p className="text-red-500 text-sm mt-2">{errors.variants}</p>}
        </div>
      )}

      {pricingType !== pricingTypes.customOnly && (
        <label className="flex items-center">
          <input
            type="checkbox"
            name="allowCustomAmount"
            checked={allowCustomAmount}
            onChange={onAllowCustomAmountChange}
            className="h-4 w-4 text-brand focus:ring-brand border-gray-300 rounded"
          />
          <span className="ml-2 text-sm font-medium text-gray-700">Allow custom amount</span>
        </label>
      )}

      {pricingType === pricingTypes.customOnly && (
        <p className="text-sm text-gray-500">Customers enter the amount.</p>
      )}
    </section>
  );
};

export default PricingConfiguration;
